import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { take } from 'lib/array/arrayUtils'
import type { ComponentProps, PropsWithChildren } from 'react'
import { useEffect, useMemo, useState } from 'react'
import OfferCard from './OfferCards/OfferCard'
import OfferCardLoadingSkeleton from './OfferCards/OfferCardLoadingSkeleton'
import OfferListOfferCard from './OfferCards/OfferListOfferCard'
import OfferListLoadingOfferCard from './OfferListTiles/OfferListLoadingOfferCard'

type OfferCardProps = ComponentProps<typeof OfferCard>
type OfferCardVariant = Exclude<OfferCardProps['variant'], undefined>
type OfferCardSize = Exclude<OfferCardProps['size'], undefined>

type TileStyle = OfferCardVariant | 'hero' | 'search' | 'frontpage'

function isOfferCardVariant(tileStyle: TileStyle): tileStyle is OfferCardVariant {
  return tileStyle.startsWith('card')
}

interface Props extends PropsWithChildren {
  inView: boolean
  offerList: App.OfferList
  /**
   * The style of card that'll be displayed
   * @default card-default
   */
  tileStyle?: TileStyle
  tileSize?: OfferCardSize
  /**
   * Pass additional query params to the offer URL generated for each card
   * NOTE: This only works for tileStyle="card" at the moment
   */
  additionalOfferUrlQuery?: string
  filters?: App.OfferListFilters
  /**
   * Number of cards to be shown at max
   * @default 20
   */
  limit?: number
  /**
   * @default limit
   */
  tabletLimit?: number | 'default'
  /**
   * Override the duration and the price with the checkIn/checkOut/Occupancy
   */
  selectedDatesAndRoomsFilter?: App.OfferListFilters
  /**
   * Whether or not to tell the offer tiles to eager load their images
   * This is mostly used for when the carousel is part of an SSR and we want them
   * shown before the app mounts.
   */
  eagerLoadImages?: boolean
}

const DEFAULT_LIMIT = 45

function OfferListCarouselTrack({
  children,
  inView,
  offerList,
  tileStyle = 'card-default',
  tileSize = 'small',
  additionalOfferUrlQuery,
  filters,
  limit = DEFAULT_LIMIT,
  tabletLimit = limit,
  selectedDatesAndRoomsFilter,
  eagerLoadImages,
}: Props) {
  const [listLimit, setListLimit] = useState<number>(limit)

  const isTabletScreenSizeAndHigher = useScreenSizeUp('tablet')
  useEffect(() => {
    if (isTabletScreenSizeAndHigher && tabletLimit) {
      setListLimit(tabletLimit === 'default' ? DEFAULT_LIMIT : tabletLimit)
    } else {
      setListLimit(limit)
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isTabletScreenSizeAndHigher])

  const offerIds = useMemo(() => take(offerList.offerIds ?? [], listLimit), [listLimit, offerList.offerIds])

  const isCardStyle = isOfferCardVariant(tileStyle)

  return <LoaderPlayStateContext.Provider value={inView ? 'running' : 'paused'}>
    {isCardStyle && <>
      {offerList.fetching && <>
        <OfferCardLoadingSkeleton variant={tileStyle} size={tileSize} />
        <OfferCardLoadingSkeleton variant={tileStyle} size={tileSize} />
        <OfferCardLoadingSkeleton variant={tileStyle} size={tileSize} />
        <OfferCardLoadingSkeleton variant={tileStyle} size={tileSize} />
      </>}
      {offerIds.map((id, index) => (
        <OfferCard
          variant={tileStyle}
          key={id}
          id={id}
          idx={index}
          // Load the second offer early for mobile carousel prep, but keep image priority on the LCP candidate only.
          lazyLoad={index > 1}
          size={tileSize}
          additionalOfferUrlQuery={additionalOfferUrlQuery}
          filters={filters}
          selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
          eagerLoadImage={index === 0 && eagerLoadImages}
          listKey={offerList.key}
        />
      ))}
    </>}
    {!isCardStyle && <>
      {offerList.fetching && <>
        <OfferListLoadingOfferCard tileStyle={tileStyle} />
        <OfferListLoadingOfferCard tileStyle={tileStyle} />
      </>}
      {offerIds.map((id, index) => <OfferListOfferCard
        key={id}
        offerId={id}
        tileStyle={tileStyle}
        position={index}
        eagerLoadImage={index === 0 && eagerLoadImages}
      />)}
    </>}
    {children}
  </LoaderPlayStateContext.Provider>
}

export default OfferListCarouselTrack
